import type { components } from "./schema";

type CachedIdentity = components["schemas"]["MeResponse"];

export const TOKEN_KEY = "maqia.crm.token";

export const SESSION_CACHE_KEY = "maqia.crm.session.v2";

const LEGACY_SESSION_CACHE_KEYS = ["maqia.crm.session"];

export function ownerKeyOf(tenantId: string, userId: string): string {
  return `${tenantId}:${userId}`;
}

export function currentOwnerKey(): string | null {
  const cached = readCachedSession();
  return cached === null ? null : ownerKeyOf(cached.user.tenant_id, cached.user.id);
}

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isCachedIdentity(value: unknown): value is CachedIdentity {
  if (!isObject(value)) {
    return false;
  }
  const user = value.user;
  if (
    !isObject(user) ||
    typeof user.id !== "string" ||
    typeof user.tenant_id !== "string" ||
    typeof user.role !== "string"
  ) {
    return false;
  }
  return (
    typeof value.plan === "string" &&
    isObject(value.capabilities) &&
    isObject(value.permissions) &&
    Array.isArray(value.enabled_lead_sources) &&
    Array.isArray(value.linked_tenants)
  );
}

export function readCachedSession(): CachedIdentity | null {
  clearLegacyCachedSessions();
  const raw = localStorage.getItem(SESSION_CACHE_KEY);
  if (raw === null) {
    return null;
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (isCachedIdentity(parsed)) {
      return parsed;
    }
  } catch {
    clearCachedSession();
    return null;
  }
  clearCachedSession();
  return null;
}

export function writeCachedSession(session: CachedIdentity): void {
  localStorage.setItem(SESSION_CACHE_KEY, JSON.stringify(session));
}

export function clearCachedSession(): void {
  localStorage.removeItem(SESSION_CACHE_KEY);
  clearLegacyCachedSessions();
}

export function clearSession(): void {
  clearToken();
  clearCachedSession();
}

function clearLegacyCachedSessions(): void {
  for (const key of LEGACY_SESSION_CACHE_KEYS) {
    localStorage.removeItem(key);
  }
}
